import classNames from "classnames";
import * as React from "react";

import { ContentCopyIcon, DoneIcon } from "@/shared/foundation/icons/mui";

import styles from "./CodeBlock.module.css";

interface CodeBlockProps {
  code: string;
  language?: string;
  className?: string;
  /**
   * When provided, renders this as the <code> content instead of raw text.
   * Use for already-highlighted HTML (e.g., from rehype-highlight).
   * `code` is still used as the clipboard source.
   */
  highlightedChildren?: React.ReactNode;
  /** Controls drawn in the header before the copy button, such as an expand toggle. */
  headerActions?: React.ReactNode;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  language,
  className,
  highlightedChildren,
  headerActions,
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(() => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // clipboard unavailable (e.g., insecure context) — silent fail
      });
  }, [code]);

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.header}>
        <span className={styles.language}>{language ?? "text"}</span>
        <span className={styles.actions}>
          {headerActions}
          <button
            className={styles.copyButton}
            onClick={handleCopy}
            aria-label="Copy code to clipboard"
            title={copied ? "Copied!" : "Copy"}
          >
            {copied ? (
              <DoneIcon className={styles.copyIcon} />
            ) : (
              <ContentCopyIcon className={styles.copyIcon} />
            )}
          </button>
        </span>
      </div>
      <pre className={styles.body}>
        {highlightedChildren ?? <code>{code}</code>}
      </pre>
    </div>
  );
};
